import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Table,
  Typography,
  Button,
  Spin,
  message,
  Space,
  Popconfirm,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { BASE_URL } from "../Components/Constant";
import ApiService from "../Components/apiService";
import CustomConnectionModal from "./CustomConnectionModal";
import CustomConnectorConfigDetail from "./CustomConnectorConfigDetail";
import CreateCustomActions from "./CreateCustomActions";

const { Title } = Typography;

const CustomConnectionsView = () => {
  const { id } = useParams(); // connector_id
  const [connector, setConnector] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null); // null → add mode
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  // Fetch connector + connections
  const fetchConnectorDetails = async () => {
    setLoading(true);
    try {
      const connectorService = new ApiService(
        `${BASE_URL}/connectors/${id}`,
        { "Content-Type": "application/json" },
        "GET"
      );
      const connectorRes = await connectorService.makeCall();
      setConnector(connectorRes);

      const connectionsService = new ApiService(
        `${BASE_URL}/connector_configs/get_by_connector_id?connector_id=${id}`,
        { "Content-Type": "application/json" },
        "GET"
      );
      const connectionsRes = await connectionsService.makeCall();

      const normalizedConnections = (connectionsRes || []).map((c) => ({
        id: c._id?.$oid || c._id,
        name: c.name || c.base_url || "Unnamed Connection",
        connectorId: c.connector_id || "",
        authType: c.auth_type || "",
        baseUrl: c.base_url || "",
        ...c, // keep full object for editing
      }));

      setConnections(normalizedConnections);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch connector details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectorDetails();
  }, [id]);

  // Delete connection
  const handleDelete = async (configId) => {
    try {
      const deleteService = new ApiService(
        `${BASE_URL}/connector_configs/${configId}`,
        { "Content-Type": "application/json" },
        "DELETE"
      );
      await deleteService.makeCall();
      message.success("Connection deleted successfully");
      fetchConnectorDetails(); // refresh list
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Failed to delete connection");
    }
  };

  const columns = [
    {
      title: "Connection Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <a
          onClick={() => {
            setSelectedConfigId(record.id);
            setDetailVisible(true);
          }}
        >
          {text}
        </a>
      ),
    },
    { title: "Connector ID", dataIndex: "connectorId", key: "connectorId" },
    { title: "Auth Type", dataIndex: "authType", key: "authType" },
    { title: "Base URL", dataIndex: "baseUrl", key: "baseUrl" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => {
              setEditingConfig(record); // pass full config
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Are you sure you want to delete this connection?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} type="link" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <Card bordered style={{ borderRadius: 8 }}>
        <Spin spinning={loading}>
          {connector && (
            <>
              <Title level={3}>{connector.name}</Title>
              <p>{connector.description}</p>

              {/* Actions Section */}
              <CreateCustomActions connectionConfigs = {connections} connectorId={id} />

              {/* Connections */}
              <Card
                title="Connections"
                style={{ marginTop: 20 }}
                extra={
                  <Button
                    type="primary"
                    onClick={() => {
                      setEditingConfig(null); // add mode
                      setModalVisible(true);
                    }}
                  >
                    Add New Connection
                  </Button>
                }
              >
                <Table
                  rowKey={(record) => record.id}
                  dataSource={connections}
                  columns={columns}
                />
              </Card>

              {/* Add/Edit modal */}
              <CustomConnectionModal
                visible={modalVisible}
                onClose={() => {
                  setModalVisible(false);
                  setEditingConfig(null);
                }}
                connectorId={id}
                config={editingConfig}
                onSuccess={fetchConnectorDetails}
              />

              {/* Config details modal */}
              <CustomConnectorConfigDetail
                visible={detailVisible}
                onClose={() => setDetailVisible(false)}
                configId={selectedConfigId}
                connectorId={id}
              />
            </>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default CustomConnectionsView;
